import React, { useState } from 'react'

import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
} from '@material-ui/core'
import { Eye as EyeIcon, EyeOff as EyeOffIcon } from 'react-feather'
import PerfectScrollbar from 'react-perfect-scrollbar'

import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from 'components/Table'
import { MenuButton, Permitted } from 'components'

import DialogChangeStatus from '../DialogChangeStatus'
import DialogDestroy from '../DialogDestroy'
import DialogForm from '../DialogForm'

import * as service from 'service'
import constants from 'constants/index'
import helpers from 'helpers'
import usePermissions from 'hooks/usePermissions'
import useSnackbar from 'hooks/useSnackbar'

import useStyles from '../../styles'

const { EXTERNAL_PLATFORM_INTEGRATIONS } = constants.permissions
const ACTION_TAGS = [
  EXTERNAL_PLATFORM_INTEGRATIONS.UPDATE,
  EXTERNAL_PLATFORM_INTEGRATIONS.DESTROY,
  EXTERNAL_PLATFORM_INTEGRATIONS.CHANGE_STATUS,
]

const SecretKeyCell = ({ secretKey, alwaysVisible = false }) => {
  const [visible, setVisible] = useState(alwaysVisible)

  if (!secretKey) {
    return '-'
  }

  return (
    <Box display="flex" alignItems="center" style={{ gap: 4 }}>
      <Typography variant="body2" style={{ fontFamily: 'monospace' }}>
        {visible
          ? secretKey
          : `${'•'.repeat(Math.max(0, secretKey.length - 8))}${secretKey.slice(
              -8,
            )}`}
      </Typography>
      {!alwaysVisible && (
        <Tooltip title={visible ? 'Ocultar chave' : 'Exibir chave'}>
          <IconButton size="small" onClick={() => setVisible((v) => !v)}>
            {visible ? <EyeOffIcon width={16} /> : <EyeIcon width={16} />}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  )
}

const TableRowIntegration = ({
  integration,
  testing,
  onTestConnection,
  setDialogChangeStatusOpen,
  setDialogDestroyOpen,
  setDialogFormOpen,
  setSelectedIntegration,
  setActive,
}) => {
  const classes = useStyles()
  const { some } = usePermissions()
  const canViewActions = some(ACTION_TAGS)
  const isSegcoop =
    integration?.externalPlatform ===
    constants.externalPlatformIntegrations.SEGCOOP_KIND
  const isSmtp =
    integration?.externalPlatform ===
    constants.externalPlatformIntegrations.SMTP_KIND

  const isActive =
    integration?.status === constants.externalPlatformIntegrations.ACTIVE_STATUS
  const canChangeStatus = [
    constants.externalPlatformIntegrations.ACTIVE_STATUS,
    constants.externalPlatformIntegrations.INACTIVE_STATUS,
  ].includes(integration?.status)

  const handleOpenChangeStatus = () => {
    setActive(isActive)
    setSelectedIntegration(integration)
    setDialogChangeStatusOpen(true)
  }

  const handleOpenDestroy = () => {
    setSelectedIntegration(integration)
    setDialogDestroyOpen(true)
  }

  const handleOpenEdit = () => {
    setSelectedIntegration(integration)
    setDialogFormOpen(true)
  }

  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body1">{integration?.id}</Typography>
      </TableCell>
      <TableCell>
        <SecretKeyCell secretKey={integration?.externalAccount} alwaysVisible />
      </TableCell>
      <TableCell>
        <SecretKeyCell secretKey={integration?.externalKey} />
      </TableCell>
      <TableCell align="center">
        <Chip
          size="small"
          className={classes.platformChip}
          label={integration?.externalPlatform}
        />
      </TableCell>
      <TableCell>
        <Typography variant="body1">
          {helpers.formatters.date(integration?.createdAt)}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Chip
          size="small"
          className={classes[integration?.status]}
          label={
            constants.externalPlatformIntegrations.STATUSES[integration?.status]
          }
        />
      </TableCell>
      {canViewActions && (
        <TableCell align="right">
          <MenuButton>
            {isSmtp && (
              <Permitted tag={EXTERNAL_PLATFORM_INTEGRATIONS.UPDATE}>
                <Button
                  color="secondary"
                  onClick={() => onTestConnection(integration?.id)}
                  disabled={testing}
                  fullWidth
                  size="small"
                >
                  Testar conexão
                </Button>
              </Permitted>
            )}
            {!isSegcoop && (
              <Permitted tag={EXTERNAL_PLATFORM_INTEGRATIONS.UPDATE}>
                <Button
                  color="secondary"
                  onClick={handleOpenEdit}
                  fullWidth
                  size="small"
                >
                  Editar
                </Button>
              </Permitted>
            )}
            {canChangeStatus && (
              <Permitted tag={EXTERNAL_PLATFORM_INTEGRATIONS.CHANGE_STATUS}>
                <Button
                  color="secondary"
                  onClick={handleOpenChangeStatus}
                  fullWidth
                  size="small"
                >
                  {isActive ? 'Inativar' : 'Ativar'}
                </Button>
              </Permitted>
            )}
            <Permitted tag={EXTERNAL_PLATFORM_INTEGRATIONS.DESTROY}>
              <Button
                color="secondary"
                onClick={handleOpenDestroy}
                fullWidth
                size="small"
              >
                Excluir
              </Button>
            </Permitted>
          </MenuButton>
        </TableCell>
      )}
    </TableRow>
  )
}

const ExternalPlatformIntegrationsTable = ({
  integrations,
  isLoading,
  onEvent,
}) => {
  const [selectedIntegration, setSelectedIntegration] = useState(null)
  const [active, setActive] = useState(false)
  const [dialogChangeStatus, setDialogChangeStatus] = useState(false)
  const [dialogDestroy, setDialogDestroy] = useState(false)
  const [dialogForm, setDialogForm] = useState(false)
  const [testingId, setTestingId] = useState(null)

  const snackbar = useSnackbar()
  const { some } = usePermissions()
  const canViewActions = some(ACTION_TAGS)

  const handleTestConnection = async (integrationId) => {
    try {
      setTestingId(integrationId)
      await service.dponet.externalPlatformIntegrations.testConnection({
        integrationId,
      })
      snackbar.open({
        message:
          'E-mail de teste enviado com sucesso! Verifique a caixa de entrada.',
        variant: 'success',
      })
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error, false),
        variant: 'error',
      })
    } finally {
      setTestingId(null)
      onEvent()
    }
  }

  return (
    <Box width="100%">
      <PerfectScrollbar
        options={{ wheelPropagation: false, useBothWheelAxes: true }}
      >
        <Table
          size="small"
          emptyMessage="Nenhuma integração encontrada"
          isLoading={isLoading}
          noWrap
        >
          <TableHead>
            <TableRow>
              <TableCell width="5%">ID</TableCell>
              <TableCell width={canViewActions ? '15%' : '18%'}>
                ID da Conta
              </TableCell>
              <TableCell width={canViewActions ? '15%' : '18%'}>
                ID da Integração
              </TableCell>
              <TableCell width={canViewActions ? '12%' : '14%'} align="center">
                Plataforma
              </TableCell>
              <TableCell width="13%">Data de Criação</TableCell>
              <TableCell width="10%" align="center">
                Status
              </TableCell>
              {canViewActions && (
                <TableCell width="10%" align="right">
                  Ações
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {integrations &&
              integrations.map((integration) => (
                <TableRowIntegration
                  key={integration.id}
                  integration={integration}
                  testing={testingId === integration.id}
                  onTestConnection={handleTestConnection}
                  setActive={setActive}
                  setSelectedIntegration={setSelectedIntegration}
                  setDialogChangeStatusOpen={setDialogChangeStatus}
                  setDialogDestroyOpen={setDialogDestroy}
                  setDialogFormOpen={setDialogForm}
                />
              ))}
          </TableBody>
        </Table>
      </PerfectScrollbar>
      {integrations && (
        <>
          <DialogChangeStatus
            active={active}
            integrationId={selectedIntegration?.id}
            open={dialogChangeStatus}
            setOpen={setDialogChangeStatus}
            onEvent={onEvent}
          />
          <DialogDestroy
            integrationId={selectedIntegration?.id}
            open={dialogDestroy}
            setOpen={setDialogDestroy}
            onEvent={onEvent}
          />
          <DialogForm
            open={dialogForm}
            setOpen={setDialogForm}
            integration={selectedIntegration}
            onEvent={onEvent}
          />
        </>
      )}
    </Box>
  )
}

export default ExternalPlatformIntegrationsTable
